import type { CardAccount } from '@bloque/sdk';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import { Separator } from '@workspace/ui/components/separator';
import { Skeleton } from '@workspace/ui/components/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@workspace/ui/components/tabs';
import { useEffect, useState } from 'react';
import { useKycGate } from '~/components/kyc-gate';
import { Layout } from '~/components/layouts/layout';
import { bloque } from '~/libs/bloque';
import { m } from '~/paraglide/messages.js';
import {
  useAccountByUrn,
  useBalance,
  useTransactions,
} from '../../-hooks/use-accounts';
import { EditCardNameDialog } from '../-components/edit-card-name-dialog';
import type { Card, CardMetadata } from '../-types/card';
import { ActionBar } from './-components/action-bar';
import { CardVisual } from './-components/card-visual';
import { ControlsTab } from './-components/controls-tab';
import { DetailsTab } from './-components/details-tab';
import { HeroBalance } from './-components/hero-balance';
import { PseTopUpDialog } from './-components/pse-topup-dialog';
import { Transactions } from './-components/transactions';
import { TransferDialog } from './-components/transfer-dialog';

export const Route = createFileRoute(
  '/_authenticated/(payment-methods)/cards/(details)/$urn',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { urn } = Route.useParams();
  const { isVerified, openKycModal, KycModal } = useKycGate();

  const { data: cardData, isLoading: isLoadingCard } =
    useAccountByUrn<Card>(urn);
  const { data: sdkCard } = useQuery({
    queryKey: ['account-sdk', urn],
    queryFn: () => bloque.accounts.get(urn),
    enabled: !!urn,
  });

  const cardAccount = sdkCard as CardAccount | undefined;
  const rawData = cardData?.data as Card | { account?: Card } | undefined;
  const card = (rawData && 'account' in rawData ? rawData.account : rawData) as
    | Card
    | undefined;
  const metadata = (card?.metadata ??
    (cardAccount?.metadata as CardMetadata) ??
    {}) as CardMetadata;

  const { data: balanceData, isLoading: isLoadingBalance } = useBalance(urn);
  const balance = balanceData?.data?.balance;
  const assetPairs = Object.keys(balance ?? {});

  const [asset, setAsset] = useState('');
  const [tab, setTab] = useState<string>('todas');
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [isPseTopUpDialogOpen, setIsPseTopUpDialogOpen] = useState(false);
  const [isEditNameDialogOpen, setIsEditNameDialogOpen] = useState(false);

  const direction =
    tab === 'entrantes' ? 'in' : tab === 'salientes' ? 'out' : undefined;

  const {
    data: transactionsResponse,
    isLoading: isLoadingTransactions,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useTransactions(urn, asset, direction);
  const transactions =
    transactionsResponse?.pages.flatMap((page) => page.transactions) ?? [];

  useEffect(() => {
    if (!asset && assetPairs.length > 0) {
      setAsset(assetPairs[0]);
    }
  }, [assetPairs]);

  const cardName =
    metadata.name ||
    (cardAccount?.metadata?.name as string) ||
    card?.details?.card_last_four ||
    'Card';
  const cardLastFour = cardAccount?.lastFour ?? card?.details?.card_last_four;
  const cardType = (cardAccount?.cardType ??
    card?.details?.card_type ??
    'VIRTUAL') as 'VIRTUAL' | 'PHYSICAL';
  const cardProvider = card?.details?.card_provider ?? 'VISA';
  const cardStatus = (
    cardAccount?.status ??
    card?.details?.card_status ??
    card?.status ??
    ''
  ).toLowerCase();

  return (
    <Layout
      title={m['cards.detail.title']()}
      subtitle={m['cards.detail.subtitle']()}
      breadcrumbs={[
        { label: m['cards.title'](), href: '/cards' },
        { label: cardName, href: `/cards/${urn}` },
      ]}
    >
      <KycModal />
      <div className="mx-auto max-w-3xl px-4 py-6">
        {/* Hero Section */}
        <div className="flex flex-col items-center gap-6">
          {isLoadingCard ? (
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="h-[233px] w-[370px] rounded-2xl" />
              <Skeleton className="h-10 w-36" />
              <Skeleton className="h-5 w-24" />
            </div>
          ) : (
            <>
              <CardVisual
                cardName={cardName}
                lastFour={cardLastFour}
                type={cardType}
                provider={cardProvider}
                status={cardStatus}
              />

              <HeroBalance
                balance={balance}
                isLoading={isLoadingBalance}
                selectedAsset={asset}
                onChangeAsset={setAsset}
                onTopUp={() => {
                  if (!isVerified) {
                    openKycModal();
                    return;
                  }
                  setIsPseTopUpDialogOpen(true);
                }}
              />

              {card && (
                <ActionBar
                  card={card}
                  cardUrn={urn}
                  isBalanceLoading={isLoadingBalance}
                  hasBalance={assetPairs.length > 0}
                  isVerified={isVerified}
                  openKycModal={openKycModal}
                  onTopUp={() => setIsPseTopUpDialogOpen(true)}
                  onTransfer={() => setIsTransferDialogOpen(true)}
                  onEditName={() => setIsEditNameDialogOpen(true)}
                />
              )}
            </>
          )}
        </div>

        <Separator className="my-8 bg-purple-500/15" />

        {/* Tabbed Content */}
        <Tabs defaultValue="activity" className="w-full">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="activity">
              {m['cards.detail.tab_activity']()}
            </TabsTrigger>
            <TabsTrigger value="details">
              {m['cards.detail.tab_details']()}
            </TabsTrigger>
            <TabsTrigger value="controls">
              {m['cards.detail.tab_controls']()}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activity" className="mt-6">
            <Transactions
              isLoading={isLoadingTransactions && transactions.length === 0}
              transactions={transactions}
              tab={tab}
              onTabChange={setTab}
            />
            {hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage
                    ? m['cards.loading']()
                    : m['common.next']()}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="details" className="mt-6">
            <DetailsTab metadata={metadata} cardUrn={urn} />
          </TabsContent>

          <TabsContent value="controls" className="mt-6">
            <ControlsTab metadata={metadata} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <TransferDialog
        isOpen={isTransferDialogOpen}
        onClose={() => setIsTransferDialogOpen(false)}
        sourceUrn={urn}
        availableAssets={assetPairs}
        balance={balance}
      />

      <PseTopUpDialog
        open={isPseTopUpDialogOpen}
        onOpenChange={setIsPseTopUpDialogOpen}
        urn={urn}
      />

      <EditCardNameDialog
        isOpen={isEditNameDialogOpen}
        onClose={() => setIsEditNameDialogOpen(false)}
        cardUrn={urn}
        currentName={metadata.name}
      />
    </Layout>
  );
}
